import { Box } from '@mui/material';
import { t } from 'i18next';
import { BackButton } from '../../../common/components/BackButton';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { bind } from '../../../common/utils/decorators';
import { JiraExport } from '../../../integrations/jira/models/JiraExport';
import { JiraRepository } from '../../../integrations/jira/services/JiraRepository';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { JiraSettingsFlow } from '../components/JiraSettingsFlow';
import { ErrorNotification, SuccessNotification } from '../../../common/utils/notifications/Notification';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { JiraAccordion } from '../components/JiraAccordion';
import { JiraProjectForm } from '../components/JiraProjectForm';
import { ComponentLoader } from '../../../common/components/ComponentLoader';

type State = BaseState & {
  initialLoad: boolean;
  isLoading: boolean;
  jiraExport: JiraExport | null;
  currentStep: number;
};

type Props = {};

export class JiraController extends BaseController<Props, State> {
  private readonly jiraRepository = new JiraRepository();
  private readonly modalManager = new ModalManager();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = {
      ...super.getBaseState(),
      initialLoad: true,
      isLoading: false,
      jiraExport: null,
      currentStep: 1,
    };
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.jiraRepository.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const permission = await this.permissionsService.canUpdateJira();
    if (!permission) {
      throw new AuthorizationError(PATHS.ORGANIZATION_DASHBOARD);
    }

    this.fetchData();
  }

  async fetchData() {
    try {
      const jiraExport = await this.jiraRepository.jiraIntegration();
      const step = jiraExport?.getJiraAccessToken() ? 3 : 1;
      this.setState({
        initialLoad: false,
        jiraExport,
        currentStep: step,
      });
    } catch (e: unknown) {
      this.setState({ initialLoad: false });
      this.handleError(e);
    }
  }

  @bind
  async onCancelClick() {
    const { confirmExit } = await this.modalManager.exitJiraConfigurationFlow();
    if (confirmExit) {
      this.navigate(PATHS.INTEGRATIONS);
    }
  }

  @bind
  onBackClick() {
    this.setState((state) => ({ ...state, currentStep: state.currentStep - 1 }));
  }

  @bind
  async onNextClick(newUrl: string) {
    const currentUrl = this.state.jiraExport?.getJiraUrl();
    if (currentUrl !== newUrl) {
      try {
        this.setState({ isLoading: true });
        const updatedJiraIntegration = await this.jiraRepository.updateJiraIssueExportUrl(newUrl);
        this.setState((state) => ({ ...state, isLoading: false, jiraExport: updatedJiraIntegration, currentStep: state.currentStep + 1 }));
        this.showMessage(new SuccessNotification(t('jiraIssueExport.baseUrlUpdated')));
      } catch (e: unknown) {
        this.setState({ isLoading: false });
        this.handleError(e);
      }
    } else {
      this.setState((state) => ({ ...state, currentStep: state.currentStep + 1 }));
    }
  }

  @bind
  async onLinkClick() {
    try {
      this.setState({ isLoading: true });
      const updatedJiraIntegration = await this.jiraRepository.authorizeJiraIntegration();
      this.setState((state) => ({ ...state, isLoading: false, jiraExport: updatedJiraIntegration, currentStep: state.currentStep + 1 }));
      const authorizationUrl = updatedJiraIntegration?.getJiraAuthorizationUrl();
      if (authorizationUrl !== null) {
        window.open(`${authorizationUrl}`, '_blank', 'noreferrer');
      }
      this.showMessage(new SuccessNotification(t('jiraIssueExport.linkEstablished')));
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onSaveClick(projectKey: string, issueType: string) {
    if (this.state.jiraExport?.getJiraAccessToken() === null) {
      await this.requestJiraAccessToken(() => this.doSave(projectKey, issueType));
    } else {
      await this.doSave(projectKey, issueType);
    }
  }

  private async doSave(projectKey: string, issueType: string) {
    try {
      this.setState({ isLoading: true });
      const updatedJiraIntegration = await this.checkAndUpdateJiraIntegration(projectKey, issueType);
      if (updatedJiraIntegration) {
        this.setState({ jiraExport: updatedJiraIntegration });
        this.showMessage(new SuccessNotification(t('jiraIssueExport.configurationSucess')));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  private async requestJiraAccessToken(then: () => Promise<void>) {
    try {
      this.setState({ isLoading: true });
      const updatedJiraIntegration = await this.jiraRepository.requestJiraIntegrationAccessToken();
      this.setState({ isLoading: false, jiraExport: updatedJiraIntegration });
      await then();
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  private async checkAndUpdateJiraIntegration(projectKey: string, issueType: string) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.jiraRepository.checkJiraIntegration(projectKey, issueType);
      if (testConnectionResult.isSuccessful()) {
        const updatedJiraIntegration = await this.jiraRepository.updateJiraIntegration(projectKey, issueType);
        this.setState({ isLoading: false });
        return updatedJiraIntegration;
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.jiraExport.testFail');
        this.showMessage(new ErrorNotification(message));
        this.setState({ isLoading: false });
        return null;
      }
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onUnlinkClick() {
    const { confirmUnlink } = await this.modalManager.unlinkIntegration('integrationList.jiraExport.title', 'jiraIssueExport.modal.unlinkTip');
    if (!confirmUnlink) {
      return;
    }
    try {
      this.setState({ isLoading: true });
      await this.jiraRepository.unlinkJiraIntegration();
      this.setState({ isLoading: false, jiraExport: null });
      this.showMessage(new SuccessNotification(t('jiraIssueExport.unlinkSuccess')));
      this.navigate(PATHS.INTEGRATIONS);
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  async onTestConnectionClick(projectKey: string, issueType: string) {
    if (this.state.jiraExport?.getJiraAccessToken() === null) {
      await this.requestJiraAccessToken(() => this.doTest(projectKey, issueType));
    } else {
      await this.doTest(projectKey, issueType);
    }
  }

  private async doTest(projectKey: string, issueType: string) {
    try {
      this.setState({ isLoading: true });
      const testConnectionResult = await this.jiraRepository.checkJiraIntegration(projectKey, issueType);
      if (testConnectionResult.isSuccessful()) {
        this.showMessage(new SuccessNotification(t('integrationList.jiraExport.testSuccess')));
      } else {
        const message = testConnectionResult.getReason() ?? t('integrationList.venafi.testFail');
        this.showMessage(new ErrorNotification(message));
      }
      this.setState({ isLoading: false });
    } catch (e: unknown) {
      this.setState({ isLoading: false });
      this.handleError(e);
    }
  }

  @bind
  onTabChange(_event: React.SyntheticEvent, newStep: number) {
    this.setState({ currentStep: newStep });
  }

  render() {
    const { initialLoad, isLoading, jiraExport, currentStep } = this.state;
    let configurationCompleted = false;
    if (jiraExport !== null) {
      configurationCompleted = !!jiraExport.getJiraAccessToken() && jiraExport.getJiraProjectKey() !== null && jiraExport.getJiraIssuetypeKey() !== null;
    }
    return this.renderBase(
      <SecondaryNav title={t(TabNameLocalizationKeys.JIRA)} subtitle={t('jiraSubtitle')} getBreadcrumbs={this.getBreadcrumbs}>
        <BackButton sx={{ mb: 2 }} />
        <JiraAccordion
          isLoading={initialLoad}
          isJiraEnabled={configurationCompleted}
          isConfigForm={true}
          details={
            configurationCompleted && !!jiraExport ? (
              <ComponentLoader loading={isLoading}>
                <Box sx={{ p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
                  <JiraProjectForm
                    jiraBaseUrl={jiraExport.getJiraUrl() ?? ''}
                    jiraProjectKey={jiraExport.getJiraProjectKey() ?? ''}
                    jiraIssueType={jiraExport.getJiraIssuetypeKey() ?? ''}
                    isJiraExportConfigured={true}
                    handleClickCancel={this.onCancelClick}
                    handleClickSave={this.onSaveClick}
                    handleClickUnlink={this.onUnlinkClick}
                    handleClickTestConnection={this.onTestConnectionClick}
                  />
                </Box>
              </ComponentLoader>
            ) : (
              <JiraSettingsFlow
                initialLoad={initialLoad}
                isLoading={isLoading}
                isJiraExportConfigured={false}
                jiraExport={jiraExport}
                step={currentStep}
                onCancelClick={this.onCancelClick}
                onBackClick={this.onBackClick}
                onNextClick={this.onNextClick}
                onLinkClick={this.onLinkClick}
                onSaveClick={this.onSaveClick}
                onUnlinkClick={this.onUnlinkClick}
                onTabChange={this.onTabChange}
                onTestConnectionClick={this.onTestConnectionClick}
              />
            )
          }
        />
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getIntegrationBreadcrumbs({
      integrationName: t(TabNameLocalizationKeys.JIRA),
      integrationPath: PATHS.INTEGRATION_JIRA,
    });
  }
}
